"use client"

import { ChevronLeft, ChevronRight, Calendar } from "@/lib/icons"

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
]

/**
 * Selector de mes con flechas atrás/adelante.
 * Navega entre meses para filtrar las métricas del dashboard.
 *
 * @param {Date} value         - Mes actualmente seleccionado (cualquier día del mes sirve)
 * @param {(d: Date) => void} onChange - Callback al cambiar de mes
 * @param {boolean} canGoForward - Si false, deshabilita el botón de "siguiente"
 */
export function MonthSelector({ value, onChange, canGoForward = true }) {
  const date = value instanceof Date ? value : new Date()
  const month = date.getMonth()
  const year = date.getFullYear()

  const now = new Date()
  const isCurrentMonth = month === now.getMonth() && year === now.getFullYear()
  const forwardDisabled = !canGoForward || isCurrentMonth

  const goPrev = () => {
    const next = new Date(year, month - 1, 1)
    onChange?.(next)
  }
  const goNext = () => {
    if (forwardDisabled) return
    const next = new Date(year, month + 1, 1)
    onChange?.(next)
  }
  const goToCurrent = () => {
    onChange?.(new Date(now.getFullYear(), now.getMonth(), 1))
  }

  return (
    <div className="inline-flex items-center gap-1 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm p-1">
      <button
        type="button"
        onClick={goPrev}
        className="h-9 w-9 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-rose-700 dark:hover:text-rose-300 transition-colors"
        title="Mes anterior"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <button
        type="button"
        onClick={goToCurrent}
        className="flex items-center gap-2 px-3 h-9 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
        title="Volver al mes actual"
      >
        <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300">
          <Calendar className="h-3.5 w-3.5" />
        </div>
        <div className="text-left">
          <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400 leading-none">
            {isCurrentMonth ? "Este mes" : "Mes seleccionado"}
          </p>
          <p className="text-[13px] font-extrabold text-slate-900 dark:text-slate-100 leading-tight">
            {MONTHS_ES[month]} {year}
          </p>
        </div>
      </button>

      <button
        type="button"
        onClick={goNext}
        disabled={forwardDisabled}
        className={`h-9 w-9 flex items-center justify-center rounded-xl transition-colors ${
          forwardDisabled
            ? "text-slate-300 dark:text-slate-700 cursor-not-allowed"
            : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-rose-700 dark:hover:text-rose-300"
        }`}
        title={forwardDisabled ? "No puedes ir al futuro" : "Mes siguiente"}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  )
}
